/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ApolloQueryResult, FetchResult } from '@apollo/client';
import { useMutation } from '@apollo/client';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { Grid, InlineNotification, TextField } from '@coursera/cds-core';
import { email as emailChecker, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';
import { Button, useTracker } from '@coursera/event-pulse/react';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import type { FindUserEmailsByUserIdQueryResult } from 'bundles/account-settings/queries';
import { getSecondaryEmail } from 'bundles/account-settings/utils/RecoveryEmailUtils';
import DeleteUserEmailMutation from 'bundles/saml-sso/components/queries/DeleteUserEmailMutation.graphql';
import type {
  InsertUserEmailMutationResult,
  InsertUserEmailMutationVariables,
} from 'bundles/saml-sso/components/queries/InsertUserEmailMutation';
import { InsertUserEmailMutation } from 'bundles/saml-sso/components/queries/InsertUserEmailMutation';
import {
  SendVerificationEmailMutation,
  type SendVerificationEmailResult,
  type SendVerificationEmailVariables,
} from 'bundles/saml-sso/components/queries/SendVerificationEmailQuery';
import type {
  UserEmails_DeleteUserEmailMutation as DeleteUserEmailMutationType,
  UserEmails_DeleteUserEmailMutationVariables as DeleteUserEmailMutationVariables,
} from 'bundles/saml-sso/components/queries/__generated__/DeleteUserEmailMutation';

import _t from 'i18n!nls/account-settings';

type Props = {
  ssoOrgName: string | undefined;
  ssoOrgSlug: string | undefined;
  refetchUserEmails: (() => Promise<ApolloQueryResult<FindUserEmailsByUserIdQueryResult>>) | undefined;
  userEmails: UserEmail[];
};

const verificationEmailSentStatus = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
} as const;

const styles = {
  notificationSpacing: css`
    margin-block-end: var(--cds-spacing-200);
  `,
  input: css`
    width: 100%;

    input[type='text']:disabled {
      background-color: var(--cds-color-neutral-disabled-weak);
      border-color: var(--cds-color-neutral-disabled-strong);
    }
  `,
  buttons: css`
    display: flex;
    margin-top: var(--cds-spacing-150);
    gap: var(--cds-spacing-200);
  `,
};

const inputName = 'recovery_email_settings_input';
const RecoveryEmail = ({ ssoOrgName, userEmails, refetchUserEmails, ssoOrgSlug }: Props) => {
  const secondaryEmail = getSecondaryEmail(userEmails);
  const [isEditable, setIsEditable] = React.useState<boolean>(true);
  const [emailFieldError, setEmailFieldError] = React.useState<string>('');
  const [verificationEmailStatus, setVerificationEmailStatus] = React.useState<
    null | keyof typeof verificationEmailSentStatus
  >(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [emailField, setEmailField] = React.useState('');
  const secondaryEmailAddress = secondaryEmail?.emailAddress ?? '';
  const hasSecondaryEmailAddress = !!secondaryEmailAddress;
  const isSecondaryEmailVerified = hasSecondaryEmailAddress && !!secondaryEmail?.isVerified;
  const showUnverifiedEmailBanner = secondaryEmail ? !isEditable && !secondaryEmail?.isVerified : false;
  const enterpriseProps = ssoOrgSlug ? { enterprise: { organizationSlug: ssoOrgSlug } } : {};
  const userId = user.get().id;
  const track = useTracker();

  React.useEffect(() => {
    if (secondaryEmailAddress) {
      setEmailField(secondaryEmailAddress);
      setIsEditable(false);
    }
  }, [secondaryEmailAddress]);

  const getValidationErrors = () => {
    const errors = runValidations(emailField, [required, emailChecker]);
    return errors;
  };

  const [sendVerificationEmail, { loading: sendVerificationEmailLoading }] = useMutation<
    SendVerificationEmailResult,
    SendVerificationEmailVariables
  >(SendVerificationEmailMutation);

  const sendVerificationEmailUtil = (recoveryEmail: string) => {
    setVerificationEmailStatus(null);
    sendVerificationEmail({
      variables: {
        input: {
          emailAddress: recoveryEmail,
          userId,
        },
      },
      onCompleted: () => {
        setVerificationEmailStatus(verificationEmailSentStatus.SUCCESS);
      },
      onError: () => {
        setVerificationEmailStatus(verificationEmailSentStatus.ERROR);
      },
    });
  };
  const [deleteUserEmail] = useMutation<DeleteUserEmailMutationType, DeleteUserEmailMutationVariables>(
    DeleteUserEmailMutation,
    {
      context: { clientName: 'gatewayGql' },
    }
  );

  const deleteUnverifiedEmails = () => {
    const deletePromises: Promise<FetchResult<DeleteUserEmailMutationType>>[] = [];
    userEmails.forEach((emailAddr: UserEmail) => {
      if (!emailAddr.isPrimary && !emailAddr.isVerified && emailAddr.emailAddress) {
        const deletePromise = deleteUserEmail({
          variables: {
            input: {
              email: emailAddr.emailAddress,
            },
          },
        });
        deletePromises.push(deletePromise);
      }
    });
    return Promise.all(deletePromises);
  };

  const [insertUserEmail] = useMutation<InsertUserEmailMutationResult, InsertUserEmailMutationVariables>(
    InsertUserEmailMutation,
    {
      onError: ({ networkError }) => {
        // @ts-expect-error(apollo-v3): TS2345: Argument of type 'readonly GraphQLError[] | undefined' is not assignable
        const statusCode = networkError?.response?.status;
        if (statusCode === 409) {
          setEmailFieldError(_t('This email is being used with another account! Try using a different email address'));
        } else {
          setEmailFieldError(_t('There was an error while adding the email, please try again'));
        }
        track('validate_text_input', {
          textInput: { name: inputName },
          status: 'failure',
          errorCode: `${statusCode ?? 'network_error'}`,
          ...enterpriseProps,
        });
      },
      onCompleted: (data) => {
        const addedEmail = data?.InsertUserEmailResponse?.userEmail?.emailAddress;
        if (addedEmail) {
          track('validate_text_input', {
            textInput: { name: inputName },
            status: 'success',
            ...enterpriseProps,
          });
          setEmailFieldError('');
          sendVerificationEmailUtil(addedEmail);
          refetchUserEmails?.();
        }
      },
    }
  );

  const onSubmitEmail = async () => {
    const errors = getValidationErrors();
    if (errors) {
      track('validate_text_input', {
        textInput: { name: inputName },
        status: 'failure',
        errorCode: 'invalid_email',
        ...enterpriseProps,
      });
      setEmailFieldError(errors);
      return;
    }

    if (emailField === secondaryEmailAddress) {
      setIsEditable(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await refetchUserEmails?.();
      if (response) {
        const updatedUserEmails = response?.data?.FindUserEmailsByUserIdResponse?.userEmails || [];
        const updatedSecondaryEmail = getSecondaryEmail(updatedUserEmails);
        if (updatedSecondaryEmail && updatedSecondaryEmail.isVerified) {
          setEmailFieldError(_t('You already have a verified recovery email'));
          return;
        }
      }
      await deleteUnverifiedEmails();
      await insertUserEmail({
        variables: {
          input: { emailAddress: emailField, isPrimary: false, userId },
        },
      });
    } catch {
      setEmailFieldError(_t('There was an error while adding the email, please try again'));
    } finally {
      setIsEditable(false);
      setIsLoading(false);
    }
  };

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmailField(value);
    setEmailFieldError('');
  };

  const allowEditing = () => {
    setIsEditable(true);
  };

  const disableEditing = () => {
    setIsEditable(false);
    setEmailFieldError('');
    setEmailField(secondaryEmailAddress);
  };

  const showCancelButton = hasSecondaryEmailAddress && isEditable;
  const showSaveButton = !hasSecondaryEmailAddress || isEditable;
  const showEditButton = !isSecondaryEmailVerified && hasSecondaryEmailAddress && !isEditable;

  return (
    <div>
      <h2 className="section-title">{_t('Recovery email')}</h2>
      <p>
        {ssoOrgName
          ? _t(
              'A personal recovery email ensures you can still access your accomplishments in case you lose access to your #{ssoOrgName} account.',
              { ssoOrgName }
            )
          : _t(
              'A personal recovery email ensures you can still access your accomplishments in case you lose access to your organization account.'
            )}
      </p>
      {showUnverifiedEmailBanner && (
        <InlineNotification
          title={_t('Verify your recovery email ')}
          severity="warning"
          css={styles.notificationSpacing}
          inlineAction={
            <Button
              size="small"
              variant="ghost"
              onClick={() => sendVerificationEmailUtil(secondaryEmailAddress)}
              loading={sendVerificationEmailLoading}
              trackingData={{
                button: { name: 'settings_resent_recovery_email_button' },
                ...enterpriseProps,
              }}
            >
              {_t('Resend email')}
            </Button>
          }
        >
          <FormattedHTMLMessage
            message={_t(
              'Check your inbox for a verification email sent to <strong>#{secondaryEmailAddress}</strong>. Verify now to avoid losing access to your #{ssoOrgName} account in the future.',
              { secondaryEmailAddress, ssoOrgName }
            )}
          />
        </InlineNotification>
      )}
      {!isEditable && verificationEmailStatus === verificationEmailSentStatus.SUCCESS && (
        <InlineNotification severity="success" css={styles.notificationSpacing}>
          {_t('We sent you a new verification email')}
        </InlineNotification>
      )}
      {!isEditable && verificationEmailStatus === verificationEmailSentStatus.ERROR && (
        <InlineNotification severity="error" css={styles.notificationSpacing}>
          {_t('We couldn’t resend the verification email. Please try again')}
        </InlineNotification>
      )}
      <TextField
        css={styles.input}
        value={emailField || ''}
        onChange={onChangeEmail}
        onBlur={getValidationErrors}
        label={_t('Recovery email address')}
        validationStatus={emailFieldError ? 'error' : undefined}
        validationLabel={emailFieldError}
        placeholder={_t('Enter your personal email address (e.g., name@gmail.com)')}
        inputProps={{
          className: 'exclude-legacy-styles',
        }}
        disabled={!isEditable}
      />

      {(showSaveButton || showEditButton || showCancelButton) && (
        <Grid css={styles.buttons} container>
          {showSaveButton && (
            <Button
              variant="secondary"
              onClick={onSubmitEmail}
              loading={isLoading}
              trackingData={{
                button: { name: 'settings_recovery_email_button' },
                ...enterpriseProps,
              }}
            >
              {_t('Save')}
            </Button>
          )}
          {showEditButton && (
            <Button
              variant="secondary"
              onClick={allowEditing}
              trackingData={{
                button: { name: 'settings_edit_recovery_email_button' },
                ...enterpriseProps,
              }}
            >
              {_t('Edit')}
            </Button>
          )}
          {showCancelButton && (
            <Button
              variant="ghost"
              onClick={disableEditing}
              trackingData={{
                button: { name: 'settings_cancel_recovery_email_button' },
                ...enterpriseProps,
              }}
            >
              {_t('Cancel')}
            </Button>
          )}
        </Grid>
      )}
    </div>
  );
};

export default RecoveryEmail;
